import { useState } from 'react';
import { reportsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, Scissors, Fuel, Truck, Activity, Wrench, FileDown } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export function ReportsPage() {
  const [turfCutsReport, setTurfCutsReport] = useState([]);
  const [fuelReport, setFuelReport] = useState([]);
  const [machineryReport, setMachineryReport] = useState([]);
  const [activitiesReport, setActivitiesReport] = useState([]);
  const [servicesReport, setServicesReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadReport = async (type) => {
    setLoading(true);
    try {
      let res;
      switch (type) {
        case 'turf-cuts':
          res = await reportsApi.getTurfCuts(startDate || undefined, endDate || undefined);
          setTurfCutsReport(res.data);
          break;
        case 'fuel':
          res = await reportsApi.getFuel(startDate || undefined, endDate || undefined);
          setFuelReport(res.data);
          break;
        case 'machinery':
          res = await reportsApi.getMachinery();
          setMachineryReport(res.data);
          break;
        case 'activities':
          res = await reportsApi.getActivities(startDate || undefined, endDate || undefined);
          setActivitiesReport(res.data);
          break;
        case 'services':
          res = await reportsApi.getServices(startDate || undefined, endDate || undefined);
          setServicesReport(res.data);
          break;
        default: break;
      }
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] ?? ''}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported');
  };

  const exportToPDF = (data, filename, title) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(title, 14, 22);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()} | Turf Farm Management`, 14, 30);
    
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(h => String(row[h] ?? '')));
    
    autoTable(doc, {
      head: [headers.map(h => h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))],
      body: rows,
      startY: 36,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [27, 67, 50], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    
    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported');
  };

  const turfCutsTotalSqm = turfCutsReport.reduce((sum, r) => sum + r.quantity_sqm, 0);
  const fuelTotalLiters = fuelReport.reduce((sum, r) => sum + r.liters, 0);
  const fuelTotalCost = fuelReport.reduce((sum, r) => sum + r.cost, 0);
  const servicesTotalCost = servicesReport.reduce((sum, r) => sum + (r.cost || 0), 0);

  const ReportActions = ({ onGenerate, onExport, onPDF, data, testPrefix }) => (
    <div className="flex gap-2">
      <Button onClick={onGenerate} disabled={loading} data-testid={`generate-${testPrefix}-btn`}>
        Generate Report
      </Button>
      <Button variant="outline" onClick={onExport} disabled={data.length === 0} data-testid={`export-${testPrefix}-btn`}>
        <Download className="h-4 w-4 mr-1" /> CSV
      </Button>
      <Button variant="outline" onClick={onPDF} disabled={data.length === 0} data-testid={`pdf-${testPrefix}-btn`}>
        <FileDown className="h-4 w-4 mr-1" /> PDF
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Reports</h1>
        <p className="text-muted-foreground">Generate and export reports for your operations</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="report-start-date" />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="report-end-date" />
            </div>
            <Button variant="outline" onClick={() => { setStartDate(''); setEndDate(''); }} data-testid="clear-dates-btn">
              Clear Dates
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="turf-cuts">
        <TabsList className="w-full justify-start bg-secondary/50 p-1">
          <TabsTrigger value="turf-cuts" className="flex items-center gap-2" data-testid="turf-cuts-report-tab">
            <Scissors className="h-4 w-4" /> Turf Cuts
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center gap-2" data-testid="activities-report-tab">
            <Activity className="h-4 w-4" /> Activities
          </TabsTrigger>
          <TabsTrigger value="machinery" className="flex items-center gap-2" data-testid="machinery-report-tab">
            <Truck className="h-4 w-4" /> Machinery
          </TabsTrigger>
          <TabsTrigger value="fuel" className="flex items-center gap-2" data-testid="fuel-report-tab">
            <Fuel className="h-4 w-4" /> Fuel
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2" data-testid="services-report-tab">
            <Wrench className="h-4 w-4" /> Services
          </TabsTrigger>
        </TabsList>

        {/* Turf Cuts Report */}
        <TabsContent value="turf-cuts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Turf Cuts Report
                </CardTitle>
                <ReportActions onGenerate={() => loadReport('turf-cuts')} onExport={() => exportToCSV(turfCutsReport, 'turf_cuts_report')} onPDF={() => exportToPDF(turfCutsReport, 'turf_cuts_report', 'Turf Cuts Report')} data={turfCutsReport} testPrefix="turf-report" />
              </div>
            </CardHeader>
            <CardContent>
              {turfCutsReport.length > 0 && (
                <div className="mb-4 p-4 bg-secondary/50 rounded-md">
                  <p className="text-lg font-heading font-semibold">
                    Total: {turfCutsTotalSqm.toLocaleString()} sqm ({(turfCutsTotalSqm / 10000).toFixed(2)} ha) from {turfCutsReport.length} records
                  </p>
                </div>
              )}
              {turfCutsReport.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Click "Generate Report" to load data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Quantity (sqm)</TableHead>
                      <TableHead>Quality</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {turfCutsReport.map((row, idx) => (
                      <TableRow key={`tc-${row.date}-${row.location}-${idx}`}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.location}</TableCell>
                        <TableCell className="font-mono">{row.quantity_sqm.toLocaleString()}</TableCell>
                        <TableCell className="capitalize">{row.quality}</TableCell>
                        <TableCell className="text-muted-foreground">{row.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activities Report */}
        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Activities Report
                </CardTitle>
                <ReportActions onGenerate={() => loadReport('activities')} onExport={() => exportToCSV(activitiesReport, 'activities_report')} onPDF={() => exportToPDF(activitiesReport, 'activities_report', 'Activities Report')} data={activitiesReport} testPrefix="activities-report" />
              </div>
            </CardHeader>
            <CardContent>
              {activitiesReport.length > 0 && (
                <div className="mb-4 p-4 bg-secondary/50 rounded-md">
                  <p className="text-lg font-heading font-semibold">{activitiesReport.length} activities recorded</p>
                </div>
              )}
              {activitiesReport.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Click "Generate Report" to load data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Equipment</TableHead>
                      <TableHead>Area (sqm)</TableHead>
                      <TableHead>Weather</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activitiesReport.map((row, idx) => (
                      <TableRow key={`act-${row.date}-${row.type}-${idx}`}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.location || '-'}</TableCell>
                        <TableCell>{row.equipment || '-'}</TableCell>
                        <TableCell className="font-mono">{row.area_sqm ? Number(row.area_sqm).toLocaleString() : '-'}</TableCell>
                        <TableCell>{row.weather_temp ? `${row.weather_temp}°C, ${row.weather_conditions}` : '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{row.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Machinery Report */}
        <TabsContent value="machinery">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Machinery Report
                </CardTitle>
                <ReportActions onGenerate={() => loadReport('machinery')} onExport={() => exportToCSV(machineryReport, 'machinery_report')} onPDF={() => exportToPDF(machineryReport, 'machinery_report', 'Machinery Report')} data={machineryReport} testPrefix="machinery-report" />
              </div>
            </CardHeader>
            <CardContent>
              {machineryReport.length > 0 && (
                <div className="mb-4 p-4 bg-secondary/50 rounded-md">
                  <p className="text-lg font-heading font-semibold">{machineryReport.length} vehicles / equipment</p>
                </div>
              )}
              {machineryReport.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Click "Generate Report" to load data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Serial Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Purchase Date</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {machineryReport.map((row, idx) => (
                      <TableRow key={`mach-${row.name}-${idx}`}>
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.model || '-'}</TableCell>
                        <TableCell className="font-mono">{row.serial_number || '-'}</TableCell>
                        <TableCell className="capitalize">{row.status?.replace('_', ' ')}</TableCell>
                        <TableCell>{row.purchase_date || '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{row.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fuel Report */}
        <TabsContent value="fuel">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Fuel Usage Report
                </CardTitle>
                <ReportActions onGenerate={() => loadReport('fuel')} onExport={() => exportToCSV(fuelReport, 'fuel_report')} onPDF={() => exportToPDF(fuelReport, 'fuel_report', 'Fuel Usage Report')} data={fuelReport} testPrefix="fuel-report" />
              </div>
            </CardHeader>
            <CardContent>
              {fuelReport.length > 0 && (
                <div className="mb-4 p-4 bg-secondary/50 rounded-md grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Fuel</p>
                    <p className="text-lg font-heading font-semibold">{fuelTotalLiters.toLocaleString()} litres</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Cost</p>
                    <p className="text-lg font-heading font-semibold">${fuelTotalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              )}
              {fuelReport.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Click "Generate Report" to load data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Litres</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fuelReport.map((row, idx) => (
                      <TableRow key={`fuel-${row.date}-${row.machinery}-${idx}`}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.machinery}</TableCell>
                        <TableCell className="font-mono">{row.liters}L</TableCell>
                        <TableCell className="font-mono">${row.cost.toFixed(2)}</TableCell>
                        <TableCell className="text-muted-foreground">{row.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Report */}
        <TabsContent value="services">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Services Report
                </CardTitle>
                <ReportActions onGenerate={() => loadReport('services')} onExport={() => exportToCSV(servicesReport, 'services_report')} onPDF={() => exportToPDF(servicesReport, 'services_report', 'Services Report')} data={servicesReport} testPrefix="services-report" />
              </div>
            </CardHeader>
            <CardContent>
              {servicesReport.length > 0 && (
                <div className="mb-4 p-4 bg-secondary/50 rounded-md">
                  <p className="text-lg font-heading font-semibold">
                    {servicesReport.length} service records — Total cost: ${servicesTotalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              )}
              {servicesReport.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Click "Generate Report" to load data</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Next Due</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {servicesReport.map((row, idx) => (
                      <TableRow key={`svc-${row.date}-${row.vehicle}-${idx}`}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.vehicle}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell className="font-mono">${(row.cost || 0).toFixed(2)}</TableCell>
                        <TableCell>{row.next_due_date || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
